import { useGetSuperAdminFranchises, useGetSuperAdminStatsPlatform } from '@agos/api-client/platform'
import { franchiseRows, primaryDomainOf, type FranchiseRow } from '@agos/saas-franchises'
import { T, formatCents, formatCount, tt } from '@agos/shared'
import { useMemo } from 'react'
import { Link } from 'wouter'

import { inactiveNote, mrrNote, platformStatsOf, revenueWindowNote, unreachableNote } from './platform-stats'
import { PageHeader, ReadFailed, StatCard, StatusBadge } from '@agos/ui'
import { ShoppingBag, Store, TrendingUp, Wallet } from 'lucide-react'

/**
 * The first screen: six figures, each with what it actually measures written next to it.
 *
 * **What the owner's 2026-09-19 report was about** - `SAAS.md §3.52`. The figures were right and
 * better than legacy's, whose dashboard hardcodes *Balíčky: 3* and shows a placeholder dash for
 * activity. What was missing was everything around them: no heading, **no loading state so an
 * unloaded page showed real-looking zeros**, no error state, and no list of tenants - legacy's
 * *Najnovšie franchisy* with its status badges is the whole bottom half of its dashboard.
 */
export function DashboardPage() {
  const { data, isLoading, isError } = useGetSuperAdminStatsPlatform()
  const stats = useMemo(() => platformStatsOf(data), [data])

  const tenants = useGetSuperAdminFranchises()
  const newest = useMemo(() => newestFirst(franchiseRows(tenants.data)), [tenants.data])

  // **A figure that is not loaded yet is a dash, not a nought.** `0` is a real answer for orders
  // today and the screen has no way to say which it meant; a dash cannot be misread.
  //
  // §3.169, FB-920 - **and a refused read is the same argument, more so.** This read `isLoading`
  // alone, so a failed load drew all four cards at nought: `platformStatsOf(undefined)` coerces an
  // absent payload to `{}` and `count()` answers 0 for anything that does not parse finitely. A load
  // in flight is over in a moment; a refusal stands until somebody reloads. One token short, on the
  // Tower's landing screen - and the newest-franchises section a hundred lines below already gates
  // its own empty state on `tenants.isError`, so the pattern was known in this file.
  const figure = (value: string) => (isLoading || isError ? '-' : value)

  return (
    <main className="space-y-4" data-dashboard>
      {/* The title block every other screen in the product opens with.
        *
        * **§3.79 corrects §3.78, which added this on a false premise.** That entry's comment read
        * *"This page had none at all: you landed on it and nothing on the screen said what it was"* -
        * and a bespoke `<header><h1>Prehľad</h1></header>` sat four lines below it. The result was
        * **two `<h1>`s reading *Prehľad*** on the Tower's landing screen, which is the sort of thing
        * that makes a finished app look unfinished, and the owner had just said so twice.
        *
        * `tower.spec.ts:3375` caught it - `[data-dashboard] h1` resolved to two elements - and it
        * caught it because a strict-mode violation is a duplicate-render alarm
        * (`agos-datatable-renders-every-row-twice`). The bespoke block is the one that goes: this
        * keeps the Dashboard consistent with the other nine screens and keeps `data-page-title`,
        * which `shell-smoke.spec.ts` asserts on every route. */}
      <PageHeader title={tt('Prehľad')} description={tt('Stav platformy na jednom mieste.')} />

      {isError && (
        <ReadFailed marker="dashboard-error"><T>Štatistiky platformy sa nepodarilo načítať.</T></ReadFailed>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* §3.81 - the product's own stat card, not a fourth reimplementation of one. The local
          * `Figure` below this file was the Tower's, and the census behind §3.78 counted `StatCard`
          * in 32 manager screens and **0** Tower ones. An icon and a colour are most of what made
          * these read as placeholders next to every other screen in the product. */}
        <StatCard
          testId="franchises"
          label={tt('Franšízy')}
          value={figure(formatCount(stats.totalFranchises))}
          icon={Store}
          color="primary"
        />
        <StatCard
          testId="mrr"
          label={tt('Mesačný príjem')}
          value={figure(mrrNote(stats))}
          icon={Wallet}
          color="emerald"
        />
        <StatCard
          testId="orders-today"
          label={tt('Objednávky dnes')}
          value={figure(formatCount(stats.ordersToday))}
          icon={ShoppingBag}
          color="blue"
        />
        <StatCard
          testId="revenue-30d"
          label={tt('Tržby')}
          value={figure(formatCents(stats.revenueLast30DaysCents))}
          sub={revenueWindowNote()}
          icon={TrendingUp}
          color="violet"
        />
      </div>
      {inactiveNote(stats) !== '' && (
        <p className="text-xs text-amber-500" data-inactive-note>
          {inactiveNote(stats)}
        </p>
      )}
      {/* §3.164, FB-874 - a tenant whose database did not answer makes the two per-tenant figures
        * above smaller with the request still succeeding, so this is the only thing on the screen
        * that can say so. Beside `inactiveNote` rather than folded into it: one is a franchise
        * switched off on purpose and the other is one that could not be reached, and a single
        * sentence for both would read as one problem where there are two. */}
      {unreachableNote(stats) !== '' && (
        <p className="text-xs text-red-500" data-unreachable-note>
          {unreachableNote(stats)}
        </p>
      )}
      <div className="rounded-2xl border border-border p-3" data-tier-mix>
        <p className="text-xs font-semibold pb-1">
          <T>Balíky</T>
        </p>
        {/* §3.169, FB-920 - `&& !isError` as well, because this is the positive claim on the screen:
          * an empty `tierMix` reads as *no franchise has a package assigned*, on the screen that
          * decides what every tenant is sold. The four figures above go to dashes; this one has to
          * go away entirely, since there is no dash for a sentence. */}
        {stats.tierMix.length === 0 && !isLoading && !isError && (
          <p className="text-xs text-muted-foreground" data-tier-mix-empty>
            <T>Žiadnej franšíze nie je priradený balík.</T>
          </p>
        )}
        {/* §3.117 part three - chips rather than a stack of plain lines, which is legacy's shape and
          * the one that reads as a *mix*: three `<p>`s one under another read as three facts.
          *
          * **Legacy's icons are deliberately not ported, and the reason is that its own map is dead.**
          * `Stats.tsx` chooses `slug === "premium" ? Crown : slug === "advanced" ? Sparkles : Zap`, and
          * the owner's packages are `basic`, `standard` and `pizzamajstro` - so **every one of the three
          * takes the `Zap` fallback** and the hierarchy the icons were for does not exist.
          * `agos-the-port-may-match-legacy-and-still-be-wrong`. Deriving an icon from the package's price
          * would work and needs a second query on a screen that has four; the count badge is what makes
          * this legible and it needs nothing. */}
        <div className="flex flex-wrap gap-1.5">
          {stats.tierMix.map((tier) => (
            <span
              key={tier.slug}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2 py-1 text-xs font-semibold"
              data-tier={tier.slug}
            >
              {tier.slug}
              <span className="rounded-full bg-muted px-1.5 text-[10px] font-bold leading-none text-muted-foreground">
                {formatCount(tier.count)}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* The bottom half of legacy's dashboard, which had no counterpart here at all. */}
      <section className="rounded-2xl border border-border p-3" data-newest-franchises>
        <div className="flex items-center justify-between pb-2">
          <p className="text-xs font-semibold">
            <T>Najnovšie franšízy</T>
          </p>
          <Link href="/franchises/new" className="text-[11px] font-bold text-primary" data-new-franchise-link>
            + <T>Nová franšíza</T>
          </Link>
        </div>

        {tenants.isLoading && (
          <p className="text-xs text-muted-foreground" data-newest-loading>
            <T>Načítavam…</T>
          </p>
        )}
        {tenants.isError && (
          <p className="text-xs text-red-500" data-newest-error>
            <T>Zoznam franšíz sa nepodarilo načítať.</T>
          </p>
        )}
        {!tenants.isLoading && !tenants.isError && newest.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground" data-newest-empty>
            <T>Zatiaľ žiadne franšízy. Vytvorte prvú.</T>
          </p>
        )}

        <div className="divide-y divide-border">
          {newest.map((row) => (
            <Link
              key={row.id}
              href={`/franchises/${row.id}`}
              className="flex items-center gap-3 py-2"
              data-newest-franchise={row.slug}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-[11px] font-bold">
                {row.slug.slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-bold">{row.name}</span>
                {/* The domain an operator would type, falling back to the slug exactly as the list
                    screen does - one franchise has no domain row at all. */}
                <span className="block truncate text-[10px] text-muted-foreground">
                  {primaryDomainOf(row) ?? row.slug}
                </span>
              </span>
              {/* §3.96 - the last hand-rolled pill in the Tower. Same shape, from the component the
                * rest of the product uses, so a state here looks like a state anywhere. */}
              <span data-newest-active={row.isActive ? 'yes' : 'no'}>
                <StatusBadge
                  label={row.isActive ? tt('Aktívna') : tt('Neaktívna')}
                  color={row.isActive ? 'emerald' : 'zinc'}
                />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  )
}

/**
 * The five most recently created, newest first.
 *
 * `franchiseRows` sorts by name so the list screen does not jump between loads; this screen wants
 * the opposite order, so it re-sorts a copy rather than asking that parser to change - two screens,
 * two orders, one parser.
 *
 * **The tie-break is the load-bearing line**, and it is the only one: two franchises created in the
 * same second would otherwise sit in whichever order the server sent, so the list would move
 * between loads for no visible reason.
 *
 * **An undated franchise sorts last, and not because of anything written here.** The first version
 * of this carried two explicit guards for it, with a comment claiming an empty string would
 * otherwise win a descending comparison. Removing them changed nothing - all 264 tests still passed
 * - because `localeCompare('')` already orders an empty string smallest, so a descending sort puts
 * it at the end. They were dead lines dressed as a rule, found by reverting them on purpose, and
 * the behaviour is pinned by a test instead.
 */
export function newestFirst(rows: FranchiseRow[], limit = 5): FranchiseRow[] {
  return [...rows]
    .sort((left, right) => {
      if (left.createdAt === right.createdAt) return left.name.localeCompare(right.name, 'sk')
      return right.createdAt.localeCompare(left.createdAt)
    })
    .slice(0, limit)
}

